/**
 * Simple Perlin noise implementation
 * Based on Ken Perlin's improved noise algorithm
 */

function fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
    return a + t * (b - a);
}

function grad(hash: number, x: number, y: number): number {
    const h = hash & 15;
    const u = h < 8 ? x : y;
    const v = h < 4 ? y : h === 12 || h === 14 ? x : 0;
    return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
}

// Simple seeded random number generator
function seededRandom(seed: number): () => number {
    let value = seed;
    return () => {
        value = (value * 9301 + 49297) % 233280;
        return value / 233280;
    };
}

/**
 * Generate permutation table from seed
 */
function generatePermutation(seed: number): number[] {
    const random = seededRandom(seed);
    const p: number[] = [];
    
    // Initialize with values 0-255
    for (let i = 0; i < 256; i++) {
        p[i] = i;
    }
    
    // Shuffle using Fisher-Yates algorithm
    for (let i = 255; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [p[i], p[j]] = [p[j], p[i]];
    }
    
    // Extend to 512 for wrapping
    const perm: number[] = [];
    for (let i = 0; i < 512; i++) {
        perm[i] = p[i & 255];
    }
    
    return perm;
}

// Default permutation table (for backward compatibility)
const defaultPerm = generatePermutation(12345);

/**
 * 2D Perlin noise function
 * @param x X coordinate
 * @param y Y coordinate
 * @param perm Optional permutation table (for seeded noise)
 * @returns Noise value between -1 and 1
 */
export function noise2D(x: number, y: number, perm: number[] = defaultPerm): number {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;

    const xf = x - Math.floor(x);
    const yf = y - Math.floor(y);

    const u = fade(xf);
    const v = fade(yf);

    const aa = perm[perm[X] + Y];
    const ab = perm[perm[X] + Y + 1];
    const ba = perm[perm[X + 1] + Y];
    const bb = perm[perm[X + 1] + Y + 1];

    const x1 = lerp(grad(aa, xf, yf), grad(ba, xf - 1, yf), u);
    const x2 = lerp(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u);

    return lerp(x1, x2, v);
}

/**
 * Get noise value normalized to 0-1 range
 */
export function noise2DNormalized(x: number, y: number, perm?: number[]): number {
    return (noise2D(x, y, perm) + 1) / 2;
}

/**
 * Create a seeded noise function
 */
export function createSeededNoise(seed: string): (x: number, y: number) => number {
    // Convert seed string to number
    let seedNum = 0;
    for (let i = 0; i < seed.length; i++) {
        seedNum = ((seedNum << 5) - seedNum) + seed.charCodeAt(i);
        seedNum = seedNum & seedNum; // Convert to 32-bit integer
    }
    
    const perm = generatePermutation(Math.abs(seedNum));
    
    return (x: number, y: number) => noise2DNormalized(x, y, perm);
}

